import React, { useMemo, useRef, useState } from 'react'
import { PageTitle, useLayout } from '../../../_metronic/layout/core'
import { axiosClientAuth } from '../../utils/axiosClient'
import moment from 'moment'
import { useSortBy, useTable } from 'react-table'
import { KTIcon } from '../../../_metronic/helpers'
import { useQuery } from 'react-query'
import { Spinner } from 'react-bootstrap'
import { useIntl } from 'react-intl'

export default function BulkReport() {
  const intl = useIntl()
  const { setCurrentBulkData } = useLayout('')

  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [searchInput, setSearchInput] = useState('')   // raw input value shown in field
  const [searchParam, setSearchParam] = useState('')   // debounced value sent to API

  // Debounce search — fire API 400ms after user stops typing
  const debounceTimer = useRef(null)
  const handleSearchChange = (value) => {
    setSearchInput(value)
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => {
      setSearchParam(value)
      setCurrentPage(1)
    }, 400)
  }

  const queryKey = ['bulk-batches', currentPage, pageSize, searchParam]

  const { data: apiResponse, isLoading, isFetching } = useQuery(
    queryKey,
    async () => {
      const params = { page: currentPage, page_size: pageSize }
      if (searchParam) params.search = searchParam

      const resp = await axiosClientAuth.get('/ip/bulk-upload/batches/', { params })
      const responseData = resp.data?.data || {}
      return {
        results: responseData?.results || [],
        count: responseData?.count || 0,
        total_pages: responseData?.total_pages || 1,
        page: responseData?.page || currentPage,
      }
    },
    { keepPreviousData: true }
  )

  const tableData = apiResponse?.results || []

  const columns = useMemo(
    () => [
      {
        Header: intl.formatMessage({ id: 'COMMON.TABLE.SERIAL_NO' }),
        accessor: 'srno',
        Cell: ({ row }) => <>{(currentPage - 1) * pageSize + row.index + 1}</>,
      },
      {
        Header: intl.formatMessage({ id: 'COMMON.TABLE.DATE' }),
        accessor: 'created_at',
        Cell: ({ value }) => (
          <>
            <span>{moment(value).format('DD MMM YYYY')}</span>
            <span className='d-block text-muted fw-semibold'>{moment(value).format('LT')}</span>
          </>
        ),
      },
      {
        Header: intl.formatMessage({ id: 'BULKREPORT.TABLE.COL_FILE_NAME' }),
        accessor: 'filename',
      },
      {
        Header: intl.formatMessage({ id: 'BULKREPORT.TABLE.COL_IP_COUNT' }),
        accessor: 'ip_count',
      },
      {
        id: 'action',
        Header: intl.formatMessage({ id: 'COMMON.TABLE.ACTION' }),
        Cell: ({ row }) => (
          <button
            className='btn h-35px w-75px btn-icon btn btn-light-primary me-3'
            onClick={() => setCurrentBulkData(row.original)}
            id='kt_drawer_bulk_ip_toggle'
          >
            {intl.formatMessage({ id: 'COMMON.BUTTON.VIEW' })}
          </button>
        ),
      },
    ],
    [currentPage, pageSize, setCurrentBulkData, intl]
  )

  // Pure display table — sorting only, no client-side filtering or pagination
  const mTable = useTable({ columns, data: tableData }, useSortBy)

  return (
    <>
      <PageTitle breadcrumbs={[]}>{intl.formatMessage({ id: 'BULKREPORT.PAGE_TITLE' })}</PageTitle>
      <div className='row g-5 g-xl-8'>
        <div className='col-12'>
          <div className='card'>
            <div className='card-header border-0 pt-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bold fs-3 mb-1'>
                  {intl.formatMessage({ id: 'BULKREPORT.TABLE.TITLE' })}
                </span>
                <span className='text-muted fw-semibold fs-7'>
                  {intl.formatMessage({ id: 'BULKREPORT.TABLE.TOTAL_FILES' }, { count: apiResponse?.count || 0 })}
                </span>
              </h3>
              <div className='d-flex align-items-center'>
                <div className='d-flex align-items-center position-relative my-1 ms-2'>
                  <KTIcon iconName='magnifier' className='text-white-500 position-absolute ms-2' />
                  <input
                    type='text'
                    className='form-control form-control-solid form-select-sm w-150px ps-9'
                    placeholder={intl.formatMessage({ id: 'BULKREPORT.TABLE.SEARCH_PLACEHOLDER' })}
                    value={searchInput}
                    onChange={(e) => handleSearchChange(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className='card-body pt-3'>
              <div className='table-responsive'>
                <table
                  {...mTable.getTableProps()}
                  className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'
                >
                  <thead>
                    {mTable.headerGroups.map((headerGroup) => (
                      <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => (
                          <th
                            className={column.id === 'action' ? 'w-200px' : ''}
                            {...column.getHeaderProps(column.getSortByToggleProps())}
                          >
                            <span className='text-gray-400 fs-7'>{column.render('Header')}</span>
                            {column.isSorted ? (
                              column.isSortedDesc ? (
                                <KTIcon iconName='down' className='text-gray ms-1' />
                              ) : (
                                <KTIcon iconName='up' className='text-gray ms-1' />
                              )
                            ) : (
                              ''
                            )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody {...mTable.getTableBodyProps()}>
                    {isLoading ? (
                      <tr>
                        <td colSpan={5} className='text-center'>
                          <Spinner animation='border' role='status' />
                        </td>
                      </tr>
                    ) : mTable.rows.length > 0 ? (
                      mTable.rows.map((row) => {
                        mTable.prepareRow(row)
                        return (
                          <tr {...row.getRowProps()}>
                            {row.cells.map((cell) => (
                              <td className='fw-bold' {...cell.getCellProps()}>
                                {cell.render('Cell')}
                              </td>
                            ))}
                          </tr>
                        )
                      })
                    ) : (
                      <tr>
                        <td colSpan={5} className='text-center'>
                          <span className='fw-bold'>
                            {intl.formatMessage({ id: 'COMMON.MESSAGES.NO_DATA_FOUND' })}
                          </span>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                <div className='d-flex justify-content-between align-items-center mt-5'>
                  <div>
                    <select
                      className='form-select form-select-sm form-select-solid'
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(parseInt(e.target.value))
                        setCurrentPage(1)
                      }}
                    >
                      {[10, 25, 50, 100].map((item) => (
                        <option key={item}>{item}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <span>
                      {intl.formatMessage({ id: 'COMMON.PAGINATION.PAGE' })}{' '}
                      <strong>
                        {apiResponse?.page || 1} {intl.formatMessage({ id: 'COMMON.PAGINATION.OF' })} {apiResponse?.total_pages || 1}
                      </strong>
                    </span>
                  </div>
                  <div>
                    <button
                      className='btn btn-sm'
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                      disabled={currentPage <= 1 || isFetching}
                    >
                      <KTIcon iconName='left' className='text-gray fs-1 me-5' />
                    </button>
                    <button
                      className='btn btn-sm'
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, apiResponse?.total_pages || 1))
                      }
                      disabled={currentPage >= (apiResponse?.total_pages || 1) || isFetching}
                    >
                      <KTIcon iconName='right' className='text-gray fs-1 me-5' />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
